import config from 'config';
import qs from 'qs';

const FF_QUERY_PARAM_KEY = 'ff';
const FF_LOCAL_STORAGE_KEY = 'featureFlags';

enum FeatureFlagEnum {
  DEBUG = 'debug',
  MOCK_REQUESTS = 'mockRequests',
  BETA_FEATURES_FOR_INTERNALS = 'betaFeaturesForInternals',
  NEW_ORY_UI = 'newOryUI',
  FINANCIAL_DOCUMENTS = 'financialDocuments',
  LOCK_STATE_TO_CONFIG = 'lockStateToConfig', // technical flag to lock feature flags state to config values and remove locals
}

type FeatureFlags = Partial<Record<FeatureFlagEnum, boolean>>;

const queryParams: { ff?: FeatureFlagEnum | FeatureFlagEnum[] } = qs.parse(window.location.search, {
  ignoreQueryPrefix: true,
});

const getFlagsFromQueryParams = (): FeatureFlags => {
  if (FF_QUERY_PARAM_KEY in queryParams) {
    const params = queryParams[FF_QUERY_PARAM_KEY];
    if (typeof params === 'string') {
      return { [params]: true };
    }
    if (Array.isArray(params)) {
      return params.reduce((acc, flag) => ({ ...acc, [flag]: true }), {});
    }
  }
  return {};
};

const getFlagsFromLocalStorage = (): FeatureFlags => {
  const flags = localStorage.getItem(FF_LOCAL_STORAGE_KEY);
  return flags ? JSON.parse(flags) : {};
};

const getFlagState = (flag: FeatureFlagEnum): boolean => {
  const configValue = !!config.featureFlags[flag];
  const localStorageValue = getFlagsFromQueryParams()[flag];
  const queryParamsValue = getFlagsFromLocalStorage()[flag];

  if (flag === FeatureFlagEnum.LOCK_STATE_TO_CONFIG) {
    return configValue;
  }

  if (typeof localStorageValue === 'undefined' && typeof queryParamsValue === 'undefined') {
    return configValue;
  } else if (typeof localStorageValue !== 'undefined') {
    return localStorageValue;
  } else if (typeof queryParamsValue !== 'undefined') {
    return queryParamsValue;
  } else {
    return false;
  }
};

if (getFlagState(FeatureFlagEnum.LOCK_STATE_TO_CONFIG)) {
  localStorage.removeItem(FF_LOCAL_STORAGE_KEY);
}

const featureFlags: FeatureFlags = Object.values(FeatureFlagEnum).reduce(
  (acc, flag) => ({ ...acc, [flag]: getFlagState(flag) }),
  {} as Record<FeatureFlagEnum, boolean>,
);

export { FeatureFlagEnum, featureFlags, FF_LOCAL_STORAGE_KEY };
